// ============================================
// File: components/modals/InstructorDetailModal.jsx
// Read-only "View" modal for an Instructor (ServiceProvider) or
// Instructor Admin (ServiceProviderAdmin) record.
// Shared by AdminList.jsx and InstructorList.jsx.
// ============================================

import React from 'react';
import { X, Mail, Phone, Building2, MapPin, Calendar, Hash } from 'lucide-react';

const STATUS_COLORS = {
  active:    '#10b981',
  inactive:  '#6b7280',
  suspended: '#ef4444'
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const Row = ({ icon: Icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.6rem 0' }}>
    <Icon size={16} color="#9ca3af" style={{ marginTop: '0.15rem', flexShrink: 0 }} />
    <div>
      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
      <div style={{ fontSize: '0.92rem', color: '#111827', marginTop: '0.1rem' }}>
        {value || '—'}
      </div>
    </div>
  </div>
);

const InstructorDetailModal = ({ instructor, onClose }) => {
  if (!instructor) return null;

  const statusColor = STATUS_COLORS[instructor.status] || '#6b7280';
  const roleLabel = instructor.role === 'ServiceProviderAdmin' ? 'Instructor Admin' : 'Instructor';

  return (
    <div className="template-modal-overlay">
      <div className="template-modal" style={{ maxWidth: '480px' }}>
        <div className="modal-header">
          <h3>{roleLabel} Details</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="template-list" style={{ padding: '0 1.5rem 1.5rem' }}>

          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            marginBottom: '0.5rem', paddingBottom: '1rem',
            borderBottom: '1px solid #f3f4f6'
          }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 700, fontSize: '1.1rem', flexShrink: 0
            }}>
              {instructor.name?.charAt(0) || '?'}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>
                {instructor.name}
              </div>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                padding: '0.15rem 0.55rem', borderRadius: '999px',
                fontSize: '0.72rem', fontWeight: 700,
                background: `${statusColor}1A`, color: statusColor
              }}>
                {instructor.status?.charAt(0).toUpperCase() + instructor.status?.slice(1)}
              </span>
            </div>
          </div>

          <Row icon={Mail}      label="Email"          value={instructor.email} />
          <Row icon={Phone}     label="Phone"          value={instructor.phone} />
          <Row icon={Building2} label="Business Name"  value={instructor.businessName} />
          <Row icon={MapPin}    label="Address"        value={[instructor.address, instructor.postcode].filter(Boolean).join(', ')} />
          <Row icon={Calendar}  label="Registered"     value={formatDate(instructor.registrationDate || instructor.createdAt)} />
          <Row icon={Hash}      label="Instructor ID"  value={instructor.instructorId} />

          <div className="form-actions" style={{ marginTop: '1rem' }}>
            <button type="button" onClick={onClose} className="btn-cancel">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstructorDetailModal;
